<script lang="ts">
  import { Button, Checkbox, Icon, Input, Select, SelectTrigger, SelectContent, SelectItem } from "@price-check/ui";
  import { Download, RefreshCw } from "lucide";
  import type { Api, Prospect, ProspectPatch, ProspectQuery } from "../lib/api";
  import Card from "../lib/ui/Card.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import Spinner from "../lib/ui/Spinner.svelte";
  import EmptyState from "../lib/ui/EmptyState.svelte";
  import ProspectRow from "./prospects/ProspectRow.svelte";
  import CallScript from "./prospects/CallScript.svelte";
  import { STATUS_LABEL, STATUS_OPTIONS, todayIso } from "../lib/prospect-labels";

  let { api }: { api: Api } = $props();

  const ALL = "all";
  const PAGE_SIZE = 50;
  let prospects = $state<Prospect[]>([]);
  let counties = $state<string[]>([]);
  let total = $state(0);
  let page = $state(1);
  let status = $state(ALL);
  let county = $state(ALL);
  let search = $state("");
  let dueOnly = $state(false);
  let loading = $state(true);
  let error = $state("");

  const pages = $derived(Math.max(1, Math.ceil(total / PAGE_SIZE)));
  const query = $derived<ProspectQuery>({
    status: status === ALL ? undefined : status,
    county: county === ALL ? undefined : county,
    q: search.trim() || undefined,
    due: dueOnly ? todayIso() : undefined,
  });

  let seq = 0;
  async function load() {
    const mine = ++seq;
    loading = true;
    try {
      const r = await api.listProspects({ ...query, page, pageSize: PAGE_SIZE });
      if (mine !== seq) return;
      prospects = r.prospects;
      counties = r.counties;
      total = r.total;
      error = "";
    } catch (e) {
      if (mine === seq) error = e instanceof Error ? e.message : "failed to load";
    } finally {
      if (mine === seq) loading = false;
    }
  }

  $effect(() => {
    void query;
    void page;
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  });

  function setFilter(apply: () => void) {
    apply();
    page = 1;
  }

  async function save(cui: string, patch: ProspectPatch) {
    try {
      const updated = await api.updateProspect(cui, patch);
      prospects = prospects.map((p) => (p.cui === cui ? updated : p));
      error = "";
    } catch (e) {
      error = e instanceof Error ? `Save failed: ${e.message}` : "Save failed";
    }
  }

  async function exportCsv() {
    try {
      const csv = await api.exportProspectsCsv(query);
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = Object.assign(document.createElement("a"), { href: url, download: `prospects-${todayIso()}.csv` });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      error = e instanceof Error ? `Export failed: ${e.message}` : "Export failed";
    }
  }
</script>

<div class="grid grid-cols-[minmax(0,1fr)_300px] gap-5 items-start max-[1100px]:grid-cols-1">
  <Card pad={false}>
    <header class="flex flex-wrap items-center gap-2 px-4.5 py-3.5 border-b border-border">
      <Input
        class="h-8 w-52"
        placeholder="Search name, city, CUI, phone"
        value={search}
        oninput={(e) => setFilter(() => (search = (e.currentTarget as HTMLInputElement).value))}
        aria-label="Search prospects"
      />
      <Select type="single" value={status} onValueChange={(v) => v && setFilter(() => (status = v))}>
        <SelectTrigger size="sm" class="w-36" aria-label="Status filter">
          {status === ALL ? "All statuses" : STATUS_LABEL[status as keyof typeof STATUS_LABEL]}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL} label="All statuses" />
          {#each STATUS_OPTIONS as o (o.value)}<SelectItem value={o.value} label={o.label} />{/each}
        </SelectContent>
      </Select>
      <Select type="single" value={county} onValueChange={(v) => v && setFilter(() => (county = v))}>
        <SelectTrigger size="sm" class="w-36" aria-label="County filter">{county === ALL ? "All counties" : county}</SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL} label="All counties" />
          {#each counties as c (c)}<SelectItem value={c} label={c} />{/each}
        </SelectContent>
      </Select>
      <label class="flex items-center gap-1.75 text-sm font-semibold cursor-pointer">
        <Checkbox checked={dueOnly} onCheckedChange={(v) => setFilter(() => (dueOnly = v === true))} aria-label="Due today" />
        Due today
      </label>
      <div class="ml-auto flex gap-2">
        <Button variant="outline" size="icon-sm" onclick={load} aria-label="Refresh"><Icon icon={RefreshCw} size={14} /></Button>
        <Button variant="outline" size="sm" onclick={exportCsv}><Icon icon={Download} size={14} />Export CSV</Button>
      </div>
    </header>

    {#if error}<div class="px-4.5 pt-3"><Banner>{error}</Banner></div>{/if}

    {#if loading && prospects.length === 0}
      <div class="py-10 grid place-items-center"><Spinner /></div>
    {:else if prospects.length === 0}
      <EmptyState
        title={dueOnly ? "Nothing due today" : "No prospects yet"}
        hint={dueOnly ? "Set a follow-up date on a row to see it here." : "Run `pnpm prospects:sync`, or enable PC_PROSPECTS_SYNC=1 on the server."}
      />
    {:else}
      <ul class="m-0 p-0 list-none divide-y divide-border">
        {#each prospects as p (p.cui)}
          <ProspectRow prospect={p} onSave={(patch) => save(p.cui, patch)} />
        {/each}
      </ul>
      <footer class="flex items-center gap-3 px-4.5 py-3 border-t border-border text-sm text-muted">
        <span>{total} prospects · page {page} of {pages}</span>
        <div class="ml-auto flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onclick={() => (page -= 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onclick={() => (page += 1)}>Next</Button>
        </div>
      </footer>
    {/if}
  </Card>
  <CallScript />
</div>
